// ==========================================
// QA SmartCases - TypeScript Types
// ==========================================

// Enums
export enum UserRole {
  GLOBAL_ADMIN = 'global_admin',
  TENANT_ADMIN = 'tenant_admin',
  ADMIN = 'admin',
  QA_LEAD = 'qa_lead',
  QA_TESTER = 'qa_tester',
  QA_AUTOMATION = 'qa_automation',
  VIEWER = 'viewer'
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending'
}

export enum JobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILED = 'failed'
}

export enum TestType {
  FUNCIONAL = 'funcional',
  INTEGRACION = 'integracion',
  BATCH = 'batch',
  PERFORMANCE = 'performance',
  REGLAS = 'reglas'
}

export enum OutputFormat {
  MANUAL = 'manual',
  GHERKIN = 'gherkin',
  ZEPHYR = 'zephyr'
}

export enum View {
  DASHBOARD = 'Dashboard',
  GENERATOR = 'Generator',
  RESULTS = 'Results',
  HISTORY = 'History',
  INGESTION = 'Ingestion',
  USERS = 'Users',
  SETTINGS = 'Settings',
  PROTOTYPES = 'Prototypes'
}

export interface ImageArtifact {
  id: string;
  project_id: string;
  filename: string;
  screen_name?: string;
  created_at: string;
  local_path: string;
  status: 'UPLOADED' | 'PROCESSING' | 'INDEXED' | 'ERROR';
  context?: string;
  hu_id?: string;
  channel?: string;
}

export interface Organization {
  id: string;
  name: string;
  settings?: Record<string, any>;
  trial_end?: string;
  is_active: boolean;
  created_at: string;
}

export interface OrganizationCreatePayload {
  id: string;
  name: string;
  settings?: Record<string, any>;
}

export interface OrganizationUpdatePayload {
  name?: string;
  settings?: Record<string, any>;
  trial_end?: string;
  is_active?: boolean;
}

// User Types
export interface User {
  id: string;
  org_id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  avatar_url?: string;
  project_ids?: string[];
  last_login?: string;
  created_at: string;
}

export interface UserCreatePayload {
  name: string;
  email: string;
  password?: string;
  role: UserRole;
  project_ids?: string[];
}

export interface UserUpdatePayload {
  name?: string;
  email?: string; // prohibited usually for updates but consistent with backend
  role?: UserRole;
  status?: UserStatus;
  password?: string; // for reset
  reset_password?: string;
  project_ids?: string[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  name: string;
  password: string;
  org_name: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export type LoginResponse = {
  user: User;
  token?: string;
  access_token?: string;
};

export type RegisterResponse = {
  user: User;
  token?: string;
  access_token?: string;
};

// Generation Types
// FlowSpecQA 2.1 Enums
export type ReviewStatus = 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'BLOCKED' | 'DEPRECATED';
export type ExecutionStatus = 'NOT_RUN' | 'PASS' | 'FAIL' | 'SKIPPED' | 'BLOCKED_EXEC';
export type EvidenceStatus = 'HAS_EVIDENCE' | 'NEEDS_EVIDENCE' | 'NOT_APPLICABLE';

export interface EvidenceRef {
  id: string;
  source_type: string;
  source_key: string;
  quote?: string;
  locator?: any;
  canonical_url?: string;
}

export interface Paso {
  id: number;
  accion: string;
  resultado_esperado: string;
  explanation?: string;
  // Legacy
  evidence?: string;
  // FlowSpecQA 2.1
  review_status?: ReviewStatus | string;
  evidence_status?: EvidenceStatus | string;
  evidence_refs?: EvidenceRef[];
}

export interface CasoPrueba {
  id_caso: string;
  titulo: string;
  tipo: string;
  objetivo: string;
  precondiciones?: string;
  datos_prueba?: string;
  pasos: Paso[];
  postcondiciones?: string;
  evidencia?: string;
  trazabilidad: string;
  prioridad: string;
  gherkin_text?: string;
  mode?: string; // "strict" or "reco"

  // Legacy Status (Maintained for compatibility)
  status?: 'draft' | 'approved' | 'blocked' | 'rework';

  // FlowSpecQA 2.1 Granular Status
  review_status?: ReviewStatus | string;
  execution_status?: ExecutionStatus | string;
  approved_by?: string;
  blocked_reason?: string;

  review_comment?: string;
  review_by?: string;
  review_at?: string;
}

export interface ResultadoCasos {
  resumen: string;
  casos: CasoPrueba[];
  cobertura: string;
  blocked_questions?: BlockedQuestion[]; // V2.2 - casos bloqueados
}

// ==========================================
// V2.2 Types - JSON Structured Output
// ==========================================

export interface Oracle {
  where: string;
  check: string;
  evidence_to_attach: string;
}

export interface TestDataItem {
  name: string;
  value: string;
  origin: 'evidence' | 'pattern' | 'assumption';
}

export interface Evidence {
  source: string;
  hu_id: string;
  chunk_id?: string;
  quote: string;
}

export interface BlockedQuestion {
  missing_for: string;
  question: string;
  why_needed: string;
  suggested_source: string;
}

export interface GenerationSummary {
  strict_count: number;
  reco_count: number;
  blocked_count: number;
  coverage_notes: string;
}


export interface GenerationRequest {
  hu_id: string;
  test_type: TestType;
  output_format: OutputFormat;
  module?: string;
  project_id?: string;
  artifact_id?: string;
  strict_mode?: boolean;
  reco_mode?: boolean;
  test_spec?: TestSpec; // Copilot Spec
}

export interface GenerationResponse {
  job_id: string;
  hu_id: string;
  test_type: TestType;
  output_format: OutputFormat;
  status: JobStatus;
  num_cases: number;
  test_cases?: string;
  structured_data?: ResultadoCasos;
  error_message?: string;
  execution_time_ms: number;
  created_at: string;
}

// History Types
export interface HistoryFilter {
  hu_id?: string;
  status?: JobStatus;
  test_type?: TestType;
  page?: number;
  page_size?: number;
}

export interface HistoryResponse {
  items: GenerationResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Dashboard Types
export interface DashboardStats {
  org_name: string;
  total_generations: number;
  success_rate: number;
  avg_cases_per_run: number;
  system_health: string;
  generations_trend: { week: string; count: number }[];
  status_distribution: {
    success: number;
    failed: number;
    processing: number;
  };
  recent_activity: ActivityItem[];
  top_fail_module?: string;
}

export interface ActivityItem {
  job_id: string;
  hu_id: string;
  status: string;
  num_cases: number;
  created_at: string;
  message: string;
}

// QA Dashboard Types
export interface ExecutionTrendItem {
  week: string;
  executions: number;
  avg_pass_rate: number;
}

export interface EntityCoverageItem {
  entity_id: string;
  entity_name?: string;
  has_code: boolean;
  has_executions: boolean;
  latest_pass_rate?: number;
  total_runs: number;
}

export interface RecentExecutionItem {
  id: string;
  entity_id?: string;
  pass_rate: number;
  total_tests: number;
  passed: number;
  failed: number;
  framework?: string;
  received_at: string;
}

export interface QADashboardStats {
  org_name: string;
  plan: string;
  total_entities: number;
  entities_with_automation: number;
  automation_coverage_pct: number;
  entities_with_executions: number;
  total_test_executions: number;
  total_tests_run: number;
  overall_pass_rate: number;
  total_test_cases_generated: number;
  total_test_suites: number;
  execution_trend: ExecutionTrendItem[];
  entities_coverage_list: EntityCoverageItem[];
  recent_executions: RecentExecutionItem[];
}

// Ingestion Types
export interface IngestionStatus {
  current_status: string;
  health_percentage: number;
  last_run?: {
    id: string;
    status: string;
    started_at: string;
    completed_at?: string;
    hus_found: number;
    pages_processed: number;
    chunks_indexed: number;
    duration_seconds?: number;
  };
  next_scheduled_run?: string;
  logs: string[];
}

// Config Types
export interface ProjectConfig {
  id: string;
  name: string;
  code?: string;
  domain?: string;
  jira_enabled: boolean;
  jira_url?: string;
  jira_query?: string;
  confluence_enabled: boolean;
  confluence_page_id?: string;
  jira_user?: string;
  jira_api_key?: string;
  confluence_user?: string;
  confluence_api_key?: string;
  ai_temperature: number;
  ai_model: string;
  max_cases: number;
  include_negative: boolean;
  include_security: boolean;
  include_edge_cases: boolean;
  include_gherkin: boolean;
  is_active: boolean;
  created_at: string;
}

export interface ProjectConfigCreate {
  name: string;
  code?: string;
  domain?: string;
  jira_enabled?: boolean;
  jira_url?: string;
  jira_query?: string;
  confluence_enabled?: boolean;
  confluence_page_id?: string;
  jira_user?: string;
  jira_api_key?: string;
  confluence_user?: string;
  confluence_api_key?: string;
  ai_temperature?: number;
  ai_model?: string;
  max_cases?: number;
  include_negative?: boolean;
  include_security?: boolean;
  include_edge_cases?: boolean;
  include_gherkin?: boolean;
}

// API Response Types
export interface ApiError {
  detail: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}
// Copilot / Test Spec Types
export interface ScenarioSkeleton {
  id: string;
  title: string;
  objective: string;
  type: string;
  priority: string;
  reasoning: string;
  is_selected?: boolean; // Frontend only state
}

export interface MissingInfo {
  topic: string;
  question: string;
  impact: string;
  suggested_value?: string;
  user_answer?: string; // Frontend only state
}

export interface SchemaField {
  name: string;
  type: string;
  required: boolean;
  example_valid?: string;
  example_invalid?: string;
}

export interface FileSchema {
  file_name: string;
  format: string;
  columns: SchemaField[];
}

export interface ValidationRule {
  rule_id: string;
  description: string;
  severity: string;
  action_on_fail: string;
}

export interface AlertSpec {
  trigger: string;
  channel: string;
  recipients: string[];
  oracle_check: string;
}

export interface OracleSpec {
  type: string; // 'log', 'db', 'api', etc.
  description: string;
  // Execution Details
  environment: string;
  access_role: string;
  command?: string;
  expected_pattern: string;
  acceptance_criteria: string;
  evidence_artifact?: string;
}

export interface TestSpec {
  hu_id: string;
  analysis_summary: string;
  detected_ac: string[];
  risks: string[];

  execution_profile: string; // 'qa_only' | 'mixed'

  // Structured Contracts
  file_schemas: FileSchema[];
  validations: ValidationRule[];
  alerts: AlertSpec[];
  oracles: OracleSpec[];

  scenarios: ScenarioSkeleton[];

  // Legacy support
  missing_info: MissingInfo[];
  quality_score?: number; // Optional until backend sends it
}

export interface AnalyzeRequest {
  hu_id: string;
  project_id?: string;
  artifact_id?: string;
}
